import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    BarChart, Bar, Legend
} from 'recharts';
import Auth, { API_URL } from '../../utils/auth';
import './SuperAdminDashboard.css';

const SUPER_ADMIN_WORKSPACE_IDS = [6, 41];

const STATUS_COLORS = {
    active: '#22c55e',
    trialing: '#3b82f6',
    past_due: '#f59e0b',
    canceled: '#ef4444',
    incomplete: '#94a3b8',
};

const PLAN_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#818cf8'];

const formatCurrency = (value) => {
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value.toFixed(2)}`;
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
    return date.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
    });
};

const SuperAdminDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        const workspaceId = Auth.getWorkspaceId();
        if (!SUPER_ADMIN_WORKSPACE_IDS.includes(Number(workspaceId))) {
            navigate('/');
            return;
        }
        fetchDashboardData();
    }, [navigate]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/stripe/admin-dashboard`);
            setData(response.data);
        } catch (err) {
            console.error('Failed to fetch Stripe dashboard:', err);
            setError('Failed to load Stripe dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="sa-dashboard">
                <div className="sa-loading">
                    <div className="sa-loading-spinner" />
                    <p>Loading Stripe dashboard...</p>
                </div>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="sa-dashboard">
                <div className="sa-error">
                    <p>{error}</p>
                    <button onClick={fetchDashboardData} className="sa-retry-btn">Retry</button>
                </div>
            </div>
        );
    }

    const {
        kpis,
        revenueByMonth,
        subscriptionsByPlan,
        subscriptionsByStatus,
        recentSubscriptions,
        recentPayments
    } = data;

    return (
        <div className="sa-dashboard">
            <div className="sa-header">
                <div>
                    <h1 className="sa-title">Stripe Dashboard</h1>
                    <p className="sa-subtitle">Super Admin &middot; Revenue & Subscription Analytics</p>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="sa-kpi-grid">
                <KpiCard
                    label="Monthly Recurring Revenue"
                    value={formatCurrency(kpis.mrr)}
                    change={kpis.mrrChange}
                    icon="revenue"
                />
                <KpiCard
                    label="Active Subscriptions"
                    value={kpis.activeSubscriptions}
                    change={kpis.activeSubsChange}
                    icon="subscriptions"
                />
                <KpiCard
                    label="Total Customers"
                    value={kpis.totalCustomers}
                    change={kpis.customersChange}
                    icon="customers"
                />
                <KpiCard
                    label="Churn Rate"
                    value={`${kpis.churnRate}%`}
                    change={kpis.churnChange}
                    icon="churn"
                    invertChange
                />
            </div>

            {/* Tabs */}
            <div className="sa-tabs">
                <button
                    className={`sa-tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button
                    className={`sa-tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subscriptions')}
                >
                    Subscriptions
                </button>
                <button
                    className={`sa-tab ${activeTab === 'payments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('payments')}
                >
                    Payments
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="sa-charts-grid">
                    {/* Revenue Trend */}
                    <div className="sa-card sa-card-wide">
                        <h3 className="sa-card-title">Revenue Trend (Last 12 Months)</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={revenueByMonth}>
                                <defs>
                                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                <YAxis tickFormatter={(v) => `$${v / 1000}k`} tick={{ fontSize: 12 }} />
                                <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} />
                                <Area
                                    type="monotone"
                                    dataKey="revenue"
                                    stroke="#6366f1"
                                    strokeWidth={2}
                                    fill="url(#revenueGrad)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Subscriptions by Plan */}
                    <div className="sa-card">
                        <h3 className="sa-card-title">Subscriptions by Plan</h3>
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie
                                    data={subscriptionsByPlan}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={90}
                                    dataKey="count"
                                    nameKey="plan"
                                    paddingAngle={3}
                                >
                                    {subscriptionsByPlan.map((entry, index) => (
                                        <Cell key={entry.plan} fill={PLAN_COLORS[index % PLAN_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Subscriptions by Status */}
                    <div className="sa-card">
                        <h3 className="sa-card-title">Subscription Status</h3>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={subscriptionsByStatus} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis type="number" tick={{ fontSize: 12 }} />
                                <YAxis dataKey="status" type="category" tick={{ fontSize: 12 }} width={80} />
                                <Tooltip />
                                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                                    {subscriptionsByStatus.map((entry) => (
                                        <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#94a3b8'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {activeTab === 'subscriptions' && (
                <div className="sa-card">
                    <h3 className="sa-card-title">Recent Subscriptions</h3>
                    <div className="sa-table-wrapper">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>Email</th>
                                    <th>Plan</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Created</th>
                                    <th>Current Period End</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentSubscriptions.map((sub) => (
                                    <tr key={sub.id}>
                                        <td className="sa-customer-name">{sub.customer?.name || '—'}</td>
                                        <td>{sub.customer?.email || '—'}</td>
                                        <td><span className="sa-plan-badge">{sub.plan}</span></td>
                                        <td>
                                            <span className={`sa-status-badge sa-status-${sub.status}`}>
                                                {sub.status}
                                            </span>
                                        </td>
                                        <td className="sa-amount">${sub.amount}/{sub.interval}</td>
                                        <td>{formatDate(sub.created)}</td>
                                        <td>{formatDate(sub.currentPeriodEnd)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'payments' && (
                <div className="sa-card">
                    <h3 className="sa-card-title">Recent Payments</h3>
                    <div className="sa-table-wrapper">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Amount</th>
                                    <th>Currency</th>
                                    <th>Status</th>
                                    <th>Description</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentPayments.map((payment) => (
                                    <tr key={payment.id}>
                                        <td>{payment.customerEmail}</td>
                                        <td className="sa-amount">${payment.amount}</td>
                                        <td className="sa-currency">{(payment.currency || 'usd').toUpperCase()}</td>
                                        <td>
                                            <span className={`sa-status-badge sa-status-${payment.status === 'succeeded' ? 'active' : 'past_due'}`}>
                                                {payment.status}
                                            </span>
                                        </td>
                                        <td className="sa-description">{payment.description || '—'}</td>
                                        <td>{formatDate(payment.created)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

const KPI_ICONS = {
    revenue: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    ),
    subscriptions: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
        </svg>
    ),
    customers: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
    ),
    churn: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" /><polyline points="16 17 22 17 22 11" />
        </svg>
    ),
};

const KpiCard = ({ label, value, change, icon, invertChange = false }) => {
    const isPositive = invertChange ? change < 0 : change > 0;
    const changeClass = isPositive ? 'sa-change-positive' : 'sa-change-negative';
    const arrow = change > 0 ? '↑' : change < 0 ? '↓' : '';

    return (
        <div className="sa-kpi-card">
            <div className={`sa-kpi-icon sa-kpi-icon--${icon}`}>
                {KPI_ICONS[icon]}
            </div>
            <div className="sa-kpi-content">
                <p className="sa-kpi-label">{label}</p>
                <p className="sa-kpi-value">{value}</p>
                {change !== undefined && (
                    <p className={`sa-kpi-change ${changeClass}`}>
                        {arrow} {Math.abs(change)}% vs last month
                    </p>
                )}
            </div>
        </div>
    );
};

export default SuperAdminDashboard;
